
"use client";

import React, { useState, useCallback, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import "./FashionHeaderMegaMenu.scss";
import { Typography } from "antd";
import { normalizeFashionNavLink } from "./fashionNavLinkUtils";


const FashionHeaderMegaMenu = ({ navigationData = [], userAgent }) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeSidebarItem, setActiveSidebarItem] = useState(null);
  const navRef = useRef(null);
  const closeTimerRef = useRef(null);

  // Instead of storing dropdownTop in state (which causes stale value on first render),
  // read it directly from the DOM ref at render time. This is always accurate.
  const getDropdownTop = useCallback(() => {
    if (navRef.current) {
      return navRef.current.getBoundingClientRect().bottom;
    }
    return 0;
  }, []);

  // We still need a trigger to re-render on resize/scroll so the dropdown repositions.
  // Use a lightweight counter to force re-render without storing the top value in state.
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const handleReposition = () => forceUpdate(n => n + 1);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, { passive: true });
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition);
    };
  }, []);

  useEffect(() => {
    if (activeMenu && navigationData) {
      const currentMenu = navigationData.find(item => item.label === activeMenu);
      if (currentMenu && currentMenu.children && currentMenu.children.length > 0) {
        setActiveSidebarItem(currentMenu.children[0].label);
      } else {
        setActiveSidebarItem(null);
      }
    }
  }, [activeMenu, navigationData]);

  const handleMenuHover = useCallback((itemLabel) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setActiveMenu(itemLabel);
  }, []);

  const handleMenuLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 100);
  }, []);

  const handleDropdownMouseEnter = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const handleDropdownMouseLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 100);
  }, []);

  const handleMenuClick = useCallback((itemLabel) => {
    setActiveMenu(activeMenu === itemLabel ? null : itemLabel);
  }, [activeMenu]);


  const renderMegaMenuContent = (item) => {
  if (!item.children || item.children.length === 0) return null;

  const hasImage = item.image && item.image.cdn_link;
  const ITEMS_PER_COLUMN = 13;

  const splitIntoColumns = (links) => {
    const columns = [];
    for (let i = 0; i < links.length; i += ITEMS_PER_COLUMN) {
      columns.push(links.slice(i, i + ITEMS_PER_COLUMN));
    }
    return columns;
  };

  return (
    <div className="fashion-mega-menu__content">
      <div className="fashion-mega-menu__columns">
        {item.children.map((child, index) => {
          // If this child has nested children (e.g. Designers list), split into sub-columns
          if (child.children && child.children.length > ITEMS_PER_COLUMN) {
            const subColumns = splitIntoColumns(child.children);
            return subColumns.map((colLinks, colIndex) => (
              <div key={`${index}-${colIndex}`} className={`fashion-mega-menu__column${colIndex > 0 ? " fashion-mega-menu__column--continuation" : ""}`}>
                {/* Only show header on the first sub-column */}
                {colIndex === 0 && (
                  <Typography className="fashion-mega-menu__column-title">
                    {child.label}
                  </Typography>
                )}
                <div className="fashion-mega-menu__column-links">
                  {colLinks.map((nestedChild, nestedIndex) => (
                    <Link
                      key={nestedIndex}
                      href={normalizeFashionNavLink(nestedChild.link) || "#"}
                      className="fashion-mega-menu__column-link"
                    >
                      {nestedChild.label}
                    </Link>
                  ))}
                </div>
              </div>
            ));
          }

          // Default: render as normal column
          return (
            <div key={index} className="fashion-mega-menu__column">
              <Typography className="fashion-mega-menu__column-title">{child.label}</Typography>
              {child.children && child.children.length > 0 && (
                <div className="fashion-mega-menu__column-links">
                  {child.children.map((nestedChild, nestedIndex) => (
                    <Link
                      key={nestedIndex}
                      href={normalizeFashionNavLink(nestedChild.link) || "#"}
                      className="fashion-mega-menu__column-link"
                    >
                      {nestedChild.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hasImage && (
        <div className="fashion-mega-menu__featured">
          <div className="fashion-mega-menu__featured-image-wrapper">
            <Link href={normalizeFashionNavLink(item.image_link) || "#"}>
              <Image
                src={item.image.cdn_link}
                alt={item.label}
                width={300}
                height={400}
                className="fashion-mega-menu__featured-image"
                priority={false}
              />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};


  const renderNavigation = () => {
    if (!navigationData || navigationData.length === 0) {
      return null;
    }

    // Read top directly from DOM — never stale, no async state delay
    const dropdownTop = getDropdownTop();

    return (
      <nav className="fashion-mega-menu__nav" ref={navRef}>
        {navigationData.map((item, index) => (
          <div
            key={index}
            className="fashion-mega-menu__item"
            onMouseEnter={() => !userAgent?.isMobile && handleMenuHover(item.label)}
            onMouseLeave={() => !userAgent?.isMobile && handleMenuLeave()}
          >
            <button
              className={`fashion-mega-menu__link ${
                activeMenu === item.label ? "fashion-mega-menu__link--active" : ""
              } ${item.label === "Sale" ? "fashion-mega-menu__link--sale" : ""}`}
              onClick={() => userAgent?.isMobile && handleMenuClick(item.label)}
            >
              {item.label}
              {item.children && item.children.length > 0 && (
                <span className="fashion-mega-menu__arrow">▼</span>
              )}
            </button>
          </div>
        ))}

        {/* Single dropdown rendered OUTSIDE the nav items loop — fixed position */}
        {activeMenu && (() => {
          const activeItem = navigationData.find(item => item.label === activeMenu);
          return activeItem && activeItem.children && activeItem.children.length > 0 ? (
            <div
              className="fashion-mega-menu__dropdown"
              style={{ top: `${dropdownTop}px` }}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            >
              {renderMegaMenuContent(activeItem)}
            </div>
          ) : null;
        })()}
      </nav>
    );
  };

  return (
    <div className="fashion-mega-menu">
      {renderNavigation()}
    </div>
  );
};

export default FashionHeaderMegaMenu;